/**
 * Copy and constants for the Get a Quote wizard.
 */

import { MONDU_HANDOFF_MODAL_CONTENT } from '../content/sharedContent';

export const WIZARD_CONTENT = {
  title: 'Get A Quote',
  subtitleEmphasis: 'No commitment, no cost',
  subtitle: ' - just a personalized quote on your invoices for all your customers.',
  actions: {
    back: 'Back',
    next: 'Next: Add Customers',
    generateQuote: 'Next: Get Quote',
    modifyCustomerSelection: 'Modify Customer Selection',
  },
  unsavedCustomerPrompt: 'You have unsaved changes. Are you sure you want to leave?',
  steps: [
    { index: 1, label: 'Business Details' },
    { index: 2, label: 'Add Customers' },
    { index: 3, label: 'Review Quote' },
  ],
  /**
   * Shown in the wizard footer when a supplier who skipped the consent modal has a quote request fail.
   * Same source as the modal's copy, so the two surfaces cannot drift apart. Only `description` is
   * rendered there — see `GetAQuoteWizard`.
   */
  requestError: MONDU_HANDOFF_MODAL_CONTENT.requestError,
} as const;

/**
 * Approved annual revenue bands, in menu order. Each closed band sends its midpoint. `Over $250M`
 * sends one dollar above its floor, so it stays above `$100M - $250M` even if that band claims
 * exactly $250M. The frame drew the second band as `$10 - $25M`; the M after 10 is required.
 */
export const ANNUAL_REVENUE_BANDS = [
  { id: '5M-10M', label: '$5M - $10M', figure: 7_500_000 },
  { id: '10M-25M', label: '$10M - $25M', figure: 17_500_000 },
  { id: '25M-50M', label: '$25M - $50M', figure: 37_500_000 },
  { id: '50M-100M', label: '$50M - $100M', figure: 75_000_000 },
  { id: '100M-250M', label: '$100M - $250M', figure: 175_000_000 },
  { id: '250M-PLUS', label: 'Over $250M', figure: 250_000_001 },
] as const satisfies readonly { id: string; label: string; figure: number }[];

export const BUSINESS_DETAILS_CONTENT = {
  heading: 'Your Business Details',
  description: 'To build an accurate quote, provide your company details.',
  fields: {
    firstName: { label: 'First Name' },
    lastName: { label: 'Last Name' },
    emailAddress: { label: 'Email Address' },
    phoneNumber: { label: 'Phone Number' },
    companyName: { label: 'Supplier Account Name' },
    addressLine1: { label: 'US Registered Address' },
    city: { label: 'City' },
    state: { label: 'State', placeholder: 'Select' },
    zipCode: { label: 'Zip Code' },
    revenueRange: { label: 'Your Annual Revenue Range (in USD)', placeholder: 'Select Range' },
  },
  errors: {
    firstName: 'Enter your first name.',
    lastName: 'Enter your last name.',
    emailAddress: 'Enter a valid email address.',
    phoneNumber: 'Enter a valid phone number.',
    companyName: 'Enter your supplier account name.',
    addressLine1: 'Enter your US registered address.',
    city: 'Enter a city.',
    state: 'Select a state.',
    zipCode: 'Enter a valid zip code.',
    revenueRange: 'Select an annual revenue range.',
  },
} as const;

export const MANUAL_CUSTOMERS_CONTENT = {
  heading: 'Add Your Customers',
  searchPlaceholder: 'Search',
  searchMenu: {
    prompt: 'Not seeing the name you expect?',
    /** Composed as `Add ${name}` on the suggestion list. */
    add: 'Add',
  },
  valueProps: [
    {
      id: 'search',
      cardClassName: 'bg-lightBlue-50',
      parts: [
        { text: "Search your top customers' names", emphasis: true },
        { text: ' then select them from the list.' },
      ],
    },
    {
      id: 'required',
      cardClassName: 'bg-lime-50',
      parts: [
        { text: 'Add ' },
        { text: 'at least one customer', emphasis: true },
        { text: ' so a quote can be generated.' },
      ],
    },
    {
      id: 'accuracy',
      cardClassName: 'bg-lightGreen-50',
      parts: [
        { text: 'The more customers you add, the ' },
        { text: 'more accurate your quote', emphasis: true },
        { text: ' will be.' },
      ],
    },
  ],
  fields: {
    name: { label: 'Customer Name' },
    street: { label: 'Street Address' },
    city: { label: 'City' },
    state: { label: 'State', placeholder: 'Select' },
    zip: { label: 'Zip Code' },
    country: { label: 'Country' },
    volume: { label: 'Expected Monthly Invoice Volume (in USD)' },
  },
  errors: {
    name: 'Enter a customer name.',
    street: 'Enter a street address.',
    city: 'Enter a city.',
    state: 'Select a state.',
    volume: 'Enter a valid amount.',
    duplicate: 'This customer is already added.',
  },
  actions: {
    save: 'Save Customer',
    /** Composed with the customer name so each row's control is distinguishable. */
    edit: 'Edit',
    remove: 'Remove',
    clearSearch: 'Clear search',
  },
  addedCustomers: {
    heading: 'Added Customers',
    nameAndAddress: 'Customer Name and Address',
    volume: 'Expected Monthly Invoice Volume (in USD)',
    actions: 'Actions',
  },
  removeCustomer: {
    /** The dialog title is this word plus the customer's name. */
    titleLead: 'Remove',
    bodyLead: 'Are you sure you want to remove ',
    bodyTail: ' from your customer list?',
    cancel: 'Cancel',
    confirm: 'Remove Customer',
  },
  /**
   * As the frame writes it, including the unfinished "XXX time". Differs from the shared pricing
   * disclaimer, which says "eligible" where this says "fundable".
   */
  disclaimer:
    'Pricing ranges shown are directional only and subject to underwriting by J.P. Morgan and their service provider, Mondu. Not every customer will be fundable. Indicative pricing only; values shown based on A-rated customers. Final rates and any credit limits are determined during the separate full underwriting process. Your provided details will carry over into the application to save you time. This quote is valid for XXX time and not retained, generate a new quote at any time.',
} as const;

/**
 * Consent modal shown before the quotes POST.
 *
 * The privacy sentence and the request-error wording are the handoff modal's, since both modals ask
 * for the same consent and fail the same way. The rest differs: this one never leaves the site, so
 * there is no third-party destination disclaimer and no redirect note.
 */
export const GET_A_QUOTE_MODAL_CONTENT = {
  title: 'Get A Quote',
  disclaimer: 'Share details with Mondu to provide a quote',
  consent: MONDU_HANDOFF_MODAL_CONTENT.consent,
  requestError: MONDU_HANDOFF_MODAL_CONTENT.requestError,
  /** Invented copy — not written or reviewed by content. Mirrors the handoff modal's message. */
  impersonationMessage: 'The supplier must generate the quote themselves so they can provide consent.',
  cancelLabel: 'Cancel',
  proceedLabel: 'Acknowledge & Proceed',
} as const;
